import Enum from 'enum';
import { fromJS } from 'immutable';
import moment from 'moment';
import isElectron from 'is-electron';
import ConferencesResource from 'flow/api/conferences';
import ProvisioningResource from 'flow/api/provisioning';
import { uuidRegExp } from 'flow/utils/meeting-constants';
import { getHoverTextShortcutMic } from 'flow/utils/call-utils';
import { getChatConference } from 'flow/actions/chat-actions';
import {
    setNumberOfMissedLogsAction,
    setLogsWhenNoNetworkAction,
    resetSavedLogs,
} from 'flow/actions/meeting-actions';
import { selectWebsocketIsClosed } from 'flow/selectors/video-selectors';
import { homeActionUrlPath, ISO_8601_MS_RESOLUTION } from 'flow/utils';
import { isChromeOrElectron, isFirefox } from 'flow/utils/browser-utils';
import {
    selectIsUserLoggedIn,
    selectNoNetworkLogs,
    selectNumberOfMissedLogs,
    selectHasNoRtcConnection,
} from 'flow/selectors/meeting-selectors';
import { addVideoLogEntry } from 'flow/utils/browser-utils';
import { sendMessage } from 'flow/api-clients/video-signaling-client';
import { getBrand } from 'flow/brands/brands';
import appConfig from 'app-config';
import { isAndroid, isIphoneOs } from 'flow/utils/os-utils-flow';
import { isMacOs, isWindowsOs } from 'flow/utils/os-utils-flow';
import { IS_UAE_HOST } from './browser-utils-TW';
import { OPEN_MEETING_IN_APP_LOCAL_STORAGE_KEY } from 'flow/utils/meeting-constants';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';
import { browserSupportStorage } from 'flow/utils/browser-storage-utils';

export const saveOpenInAppPreference = (openInApp) => {
    if (!browserSupportStorage()) {
        return;
    }

    wrappedLocalStorage.setItem(
        OPEN_MEETING_IN_APP_LOCAL_STORAGE_KEY,
        JSON.stringify(openInApp),
    );
};

export const getSavedOpenInAppPreference = () => {
    if (
        !browserSupportStorage() ||
        !wrappedLocalStorage.isSet(OPEN_MEETING_IN_APP_LOCAL_STORAGE_KEY)
    ) {
        return null;
    }

    try {
        return JSON.parse(
            wrappedLocalStorage.getItem(OPEN_MEETING_IN_APP_LOCAL_STORAGE_KEY),
        );
    } catch (_error) {
        return null;
    }
};

export const shouldShowOpenInAppModal = (isJoiningThroughLink) => {
    if (
        !browserSupportStorage() ||
        !isJoiningThroughLink ||
        isElectron() ||
        isAndroid() ||
        isIphoneOs()
    ) {
        return false;
    }

    const preference = getSavedOpenInAppPreference();
    return preference === null;
};

export const shouldAutoOpenInApp = (isJoiningThroughLink) => {
    if (!browserSupportStorage() || !isJoiningThroughLink || isElectron()) {
        return false;
    }

    const preference = getSavedOpenInAppPreference();
    return preference === true;
};

const openMeetingInvitationMail = (
    meetingCode,
    pin,
    number,
    i18next,
    hasVideoCapability,
    countryCode,
) => {
    const mailToSubject = i18next.t('videoconference.email.subject');
    const conferenceLink = getConferenceLink(meetingCode, countryCode);
    const conferenceNumberURL = getBrand().brandConfNumberUrl;

    const moreNumbersString = conferenceNumberURL
        ? i18next.t('videoconference.email.body.more_numbers') +
          ' ' +
          conferenceNumberURL
        : '';
    const body =
        `${i18next.t('videoconference.email.body.invitation')}%0A%0A` +
        (hasVideoCapability
            ? `${i18next.t(
                  'videoconference.email.body.join_video',
              )} ${encodeURIComponent(conferenceLink)}%0A%0A`
            : '') +
        `${i18next.t(
            'videoconference.email.body.join_phone',
        )} ${number}, ${pin}%0A` +
        moreNumbersString;
    window.open(`mailto:?subject=${mailToSubject}&body=${body}`, '_blank');
};

export const getMeetingCode = (entityKey, meetingType) => async (dispatch) => {
    if (entityKey && uuidRegExp.test(entityKey)) {
        addMeetingLog(
            `EntityKey was a meeting code: '${entityKey}' - returning it`,
            MeetingLogLevel.WARNING,
        );
        return entityKey;
    }

    let meetingCode;
    const parsedParams = parseGetParams();

    if (parsedParams && parsedParams.roomName) {
        meetingCode = parsedParams.roomName;
    } else {
        // If there is no room param in URL, try entityKey
        const entityKeyType = entityKey && entityKey.split('-')[0]; //Extract split to util?

        if (entityKeyType === 'chatsession') {
            const result = await dispatch(
                getChatConference(entityKey, meetingType),
            );
            meetingCode = result && result.meetingCode;
        } else if (entityKeyType === 'userconference') {
            const conferences = await new Promise((resolve, reject) => {
                ConferencesResource.getConferences(
                    { onResult: resolve, onError: reject },
                    'contact,live',
                );
            });
            const conferenceDTO =
                conferences &&
                fromJS(conferences).find(
                    (conference) => conference.get('key') === entityKey,
                );
            meetingCode = conferenceDTO && conferenceDTO.get('meetingCode');
        } else {
            throw new Error('Wrong entity type');
        }
    }
    if (!meetingCode) {
        throw new Error(
            `Could not resolve a meeting code for entityKey '${entityKey}'`,
        );
    }
    return meetingCode;
};

export const getIfValidBrowser = () => {
    if (isIphoneOs() || isAndroid()) {
        return true;
    }
    return isChromeOrElectron() || isFirefox();
};

export const getMeetingDataFromAPI = (meetingCode, isLoggedIn) => {
    return new Promise((resolve, reject) => {
        const resultCallback = (result) => {
            const conferenceData = fromJS({
                conferenceMembers: result.conferenceMembers,
                number: result.number,
                pin: result.pin,
                name: result.name,
                state: result.state,
                chatSessionId: result.chatSessionId,
                meetingCode: result.meetingCode,
                meetingType: result.meetingType,
                videoConferenceConfig: result.videoConferenceConfig,
                meetingAccess: result.meetingAccess,
                requireHostPresent: result.requireHostPresent,
                hostPresent: result.hostPresent,
                meetingJoinBlockedReason: result.meetingJoinBlockedReason,
                viewerIsMeetingHost: result.viewerIsMeetingHost,
                viewerIsMeetingAdmin: result.viewerIsMeetingAdmin,
            });
            resolve(conferenceData);
        };

        if (isLoggedIn) {
            ConferencesResource.getMeetingCodeConference(
                {
                    onResult: (result) => resultCallback(result),
                    onError: (error) => reject(error),
                    onClientError: (error) => reject(error),
                },
                meetingCode,
                'contact,live',
            );
        } else {
            ConferencesResource.getMeetingCodeConferenceExternal(
                {
                    onResult: (result) => resultCallback(result),
                    onError: (error) => reject(error),
                    onClientError: (error) => reject(error),
                },
                meetingCode,
                'contact,live',
            );
        }
    });
};

/**
 * Makes an API call to get which kms server to use.
 * OBS! We assume only one server is returned.
 */
export const getKmsServerFromConfig = () => (dispatch) => {
    return new Promise((resolve, reject) => {
        ProvisioningResource.getKmsServers({
            onResult: (result) => {
                const kmsServer = fromJS(result[0]);
                return resolve(kmsServer);
            },
            onError: (error) => {
                dispatch(
                    addMeetingLog(
                        `MeetingUtils#getKmsServerFromConfig Failed to fetch KMS servers. Error: ${error}`,
                        MeetingLogLevel.ERROR,
                    ),
                );
                return reject(error);
            },
        });
    }).catch(() =>
        dispatch(
            addMeetingLog(
                'MeetingUtils#getKmsServerFromConfig Could not get Kms servers',
                MeetingLogLevel.ERROR,
            ),
        ),
    );
};

/**
 * Takes a string, `${UUID}-${name}`, and returns
 * `${name}`
 */
export const sliceUUIDFromUserName = (name) => {
    if (!name) {
        return '';
    }
    return uuidRegExp.test(name.slice(0, 36)) ? name.slice(37) : name;
};

/**
 * Based on a members name, return a number which will determine the
 * color of the avatar circle when a member has no video
 */
export const createFourDigitFromString = (name) => {
    const numberArray = [];
    const firstName = name && name.substring(0, 4);
    for (let i = 0; i < 4; i++) {
        // Guard short names: firstName[i] is undefined past the string's end.
        const letter = firstName?.[i]?.charCodeAt(0) ?? '';
        numberArray.push(letter);
    }
    return numberArray.join('');
};

/**
 * Return the first 3 members names and then remaining members as '+ numberOfMembers'
 */
export const getFirstThreeMembersNames = (nameList) => {
    const nameSlice = nameList && nameList.slice(0, 3);
    let names = nameSlice && nameSlice.join(', ');
    if (nameList.length > 3) {
        const numberExtraMembers = nameList.length - 3;
        names += ` +${numberExtraMembers}`;
    }
    return names;
};

/**
 * See: https://stackoverflow.com/a/30106551/9191450
 */
export const b64EncodeUnicode = (str) => {
    return btoa(
        encodeURIComponent(str).replace(
            /%([0-9A-F]{2})/g,
            function toSolidBytes(_match, p1) {
                return String.fromCharCode('0x' + p1);
            },
        ),
    );
};

export const openMeetingInvitationMailWithPin = async (
    meetingCode,
    inputPin,
    number,
    i18next,
    hasVideoCapability,
    countryCode,
) => {
    let pin = inputPin;
    if (!inputPin) {
        pin = await getPinCodeFromAPI(meetingCode);
    }
    openMeetingInvitationMail(
        meetingCode,
        pin,
        number,
        i18next,
        hasVideoCapability,
        countryCode,
    );
};

export const openMeetingLobbyInApplicationFromBrowser = (meetingCode) => {
    if (isIphoneOs() || isAndroid()) {
        //noop
    } else {
        window.open(`telavox://video?room=${meetingCode}`, '_parent');
        return null;
    }

    if (IS_UAE_HOST) return null;

    const downloadUrl = getBrand().brandAppDownloadUrl;

    if (downloadUrl) {
        return window.open(downloadUrl, '_blank');
    }

    return null;
};

export const getPinCodeFromAPI = async (meetingCode) => {
    if (!meetingCode) {
        console.warn(
            'No meetingCode was provided to getPinCodeFromMeetingCode',
        );
        return undefined;
    }
    try {
        const fetchUrl = homeActionUrlPath(
            `/api/external/conference/${meetingCode}/pin`,
        );
        const postResult = await fetch(fetchUrl, { method: 'post' });
        const postResultJson = await postResult.json();
        return postResultJson.pin;
    } catch (e) {
        console.error(
            `Error fetching members for meetingCode ${meetingCode}:`,
            e,
        );
        return undefined;
    }
};

export const rotateMeetingJoiningData = async (meetingCode) => {
    if (!meetingCode) {
        console.warn('No meetingCode was provided to rotateMeetingData');
        return undefined;
    }
    return new Promise((resolve, reject) => {
        ConferencesResource.rotateConferenceJoiningInfo(
            {
                onResult: ({ meetingCode, pin }) => {
                    resolve([meetingCode, pin]);
                },
                onError: reject,
            },
            meetingCode,
        );
    });
};

export const MeetingLogLevel = new Enum(['WARNING', 'ERROR']);

const MAX_SIZE_LOGS = 100;
export const addMeetingLog =
    (logParam, level = 'info', skipSending = false) =>
    (dispatch, getState) => {
        const socketIsClosed = selectWebsocketIsClosed(getState());
        const hasNoRtcConnection = selectHasNoRtcConnection(getState());

        if (socketIsClosed || hasNoRtcConnection) {
            dispatch(saveLog(logParam, skipSending));
        } else {
            const savedLogs = selectNoNetworkLogs(getState());
            if (savedLogs && savedLogs.length) {
                dispatch(sendLog(savedLogs, false));
                dispatch(resetSavedLogs());
            }
            dispatch(sendLog(logParam, skipSending));
        }

        switch (level) {
            case MeetingLogLevel.WARNING: {
                console.warn(logParam);
                break;
            }
            case MeetingLogLevel.ERROR: {
                console.error(logParam);
                break;
            }
            default: {
                console.info(logParam);
                break;
            }
        }
    };

export const saveLog = (log, skipSending) => (dispatch, getState) => {
    if (!log) {
        return;
    }
    if (!skipSending) {
        const newLogs = [];
        const savedLogs = selectNoNetworkLogs(getState());
        if (savedLogs.length === MAX_SIZE_LOGS) {
            const numberOfMissedLogs = selectNumberOfMissedLogs(getState());
            dispatch(setNumberOfMissedLogsAction(numberOfMissedLogs + 1));
            savedLogs.splice(0, 2);
            newLogs.push(`There are ${numberOfMissedLogs + 1} missed logs`);
        }
        newLogs.push(...savedLogs);
        const date = moment().format(ISO_8601_MS_RESOLUTION);
        const logWithDate = `${date} ${log}`;
        newLogs.push(logWithDate);
        dispatch(setLogsWhenNoNetworkAction(newLogs));
    }
};

export const sendLog = (log, skipSending) => (dispatch, getState) => {
    if (!log) {
        return;
    }
    const userIsLoggedIn = selectIsUserLoggedIn(getState());
    if (userIsLoggedIn) {
        addVideoLogEntry(log);
    }

    if (!skipSending) {
        dispatch(sendMessage({ id: 'log', message: log }));
    }
};

export const getConferenceLink = (meetingCode, countryCode) => {
    const baseURL = getBaseURL();
    if (!baseURL) return null;
    const url = `${baseURL}/video?room=${meetingCode}`;
    return countryCode ? `${url}&countryCode=${countryCode}` : url;
};

/**
 * Formats a PIN for display as two space-separated groups, e.g. "123 456"
 * for a 6-digit PIN. Strips whitespace from the raw value first — callers
 * aren't all guaranteed to hand over a space-free string — and splits by
 * the PIN's own length instead of assuming 6 digits, since 4-digit PINs
 * also occur (AC-1834 review).
 */
export const formatPin = (pin) => {
    const digits = pin.replace(/\s/g, '');
    const mid = Math.ceil(digits.length / 2);
    return `${digits.slice(0, mid)} ${digits.slice(mid)}`;
};

export const hasMediaPermissions = async () => {
    try {
        const [mic, camera] = await Promise.all([
            navigator.permissions.query({ name: 'microphone' }),
            navigator.permissions.query({ name: 'camera' }),
        ]);
        return mic.state === 'granted' && camera.state === 'granted';
    } catch {
        return false;
    }
};

const getBaseURL = () => {
    if (appConfig.environment === 'dev') {
        return 'http://localhost:8080';
    } else {
        return appConfig.environment === 'beta'
            ? 'https://flowapp-beta.telavox.com'
            : getBrand().brandAppUrl;
    }
};

export const getLoginLink = (meetingCode) => {
    /* TODO: #video For "external desktop app users" in the future (Need deep link for app)
    if (isElectron()) {
        // GO TO /signin
    }*/

    const baseURL = getBaseURL();
    if (!baseURL) {
        return null; // Return null if no base URL is available
    }
    const videoLink = meetingCode ? `video?room=${meetingCode}` : '';
    const redirectLink = `${baseURL}/${videoLink}`;
    const passwordActionUrl = getBrand().passwordActionUrl;
    const loginHost = passwordActionUrl;
    if (!loginHost) {
        return null; // Return null if no login host is available
    }
    return `${loginHost}/flowin.jsp?url=${redirectLink}`;
};

export const getSettingsPath = (extension) => {
    return `/settings/${extension}/softphone`;
};

const ROOM_PARAM_DESCRIPTOR = 'room=';
const NAME_PARAM_DESCRIPTOR = 'name=';
const ACCOUNT_ID_PARAM_DESCRIPTOR = 'ai=';
const KMS_PARAM = 'k=';
const PORT_PARAM = 'p=';
const BETA_PARAM = 'beta=';
const ALPHA_PARAM = 'alpha=';
const VIDEO_MUTED_PARAM = 'vmute=';
const AUDIO_MUTED_PARAM = 'amute=';
const SIGNALLING_SERVER = 'k=';
export const AUTO_JOIN_PARAM = 'j=';
export const DEBUG_PARAM = 'debug=';

/**
 * The senderName stored in Redux is `${uuid}-${actualName}` — the UUID prefix
 * exists for Kurento participant disambiguation (see `setSenderName` in
 * meeting-actions.js). Other systems that don't care about Kurento's id model
 * (e.g. the video-chat side-channel) want just the human name.
 *
 * UUIDs are 36 chars (8-4-4-4-12 hex + 4 dashes) + 1 separating '-' = 37.
 * If the input doesn't match the expected shape we return it unchanged so
 * callers stay safe with whatever they pass in.
 */
const SENDER_NAME_UUID_PREFIX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i;

export const stripSenderNameUuidPrefix = (senderName) => {
    if (!senderName) {
        return senderName;
    }
    return SENDER_NAME_UUID_PREFIX.test(senderName)
        ? senderName.substring(37)
        : senderName;
};

/**
 * The inverse of stripSenderNameUuidPrefix: pulls out the join UUID itself
 * (undefined if senderName doesn't match the `${uuid}-${name}` shape). This
 * is the same UUID home expects at the participant-identity endpoint (VID-264)
 * — it parses it back out of each member's displayName to look up their
 * captured identity.
 */
export const extractSenderNameUuid = (senderName) => {
    if (!senderName) {
        return undefined;
    }
    return SENDER_NAME_UUID_PREFIX.test(senderName)
        ? senderName.substring(0, 36)
        : undefined;
};

export const parseGetParams = () => {
    const params = window.location.search.substring(1).split(/&/g);
    return {
        roomName: getParamValue(params, ROOM_PARAM_DESCRIPTOR),
        userName: getParamValue(params, NAME_PARAM_DESCRIPTOR),
        accountId: getParamValue(params, ACCOUNT_ID_PARAM_DESCRIPTOR),
        autoJoin: getParamValue(params, AUTO_JOIN_PARAM),
        kmsServer: getParamValue(params, KMS_PARAM),
        port: getParamValue(params, PORT_PARAM),
        beta: getParamValue(params, BETA_PARAM),
        alpha: getParamValue(params, ALPHA_PARAM),
        videoMuted: getParamValue(params, VIDEO_MUTED_PARAM),
        audioMuted: getParamValue(params, AUDIO_MUTED_PARAM),
        signallingServer: getParamValue(params, SIGNALLING_SERVER),
        debug: getParamValue(params, DEBUG_PARAM),
    };
};
/**
 * Enables per-peer WebRTC stats collection and the tile read-outs that render
 * from it. Both sides share this gate: without it no peer is registered for
 * stats, so a read-out would have nothing to show anyway.
 *
 * `?debug=true` collects, displays and uploads the aggregate to Home;
 * `?debug=local` collects and displays only — see isVideoStatsUploadEnabled.
 */
export const isVideoStatsDebugEnabled = () => {
    const { debug } = parseGetParams();
    return debug === 'true' || debug === 'local';
};

/**
 * Whether collected stats are uploaded to Home's metrics endpoint.
 *
 * On for everyone except `?debug=local`: sessions without a debug param
 * register no peers, but they still upload the disconnect-only aggregate the
 * meeting monitor counts for every real user — an opt-in upload gate would
 * silently drop webrtc_disconnect_count from production. `?debug=local` is the
 * load-test mode: bots run the real webclient and need the collection (that is
 * what the tile read-outs display), but nothing they measure — disconnects
 * included — may pour synthetic numbers into the pipeline that real-user
 * metrics are read from.
 */
export const isVideoStatsUploadEnabled = () =>
    parseGetParams().debug !== 'local';

const getParamValue = (params, searchString) => {
    const length = searchString.length;
    const regExp = new RegExp(`^${searchString}`);
    const value = params.find((object) => regExp.test(object)) || '';
    return decodeURI(value.substring(length));
};

export const getTextHoverChat = (
    chatAvailable,
    isFullscreenView,
    chatIsOpen,
    i18next,
) => {
    if (!chatAvailable) {
        return i18next.t('videoconference.controlpanel.no_chat_available');
    }
    if (!isFullscreenView) {
        return i18next.t('videoconference.controlpanel.go_to_chat');
    }
    if (chatIsOpen) {
        return i18next.t('videoconference.controlpanel.close_chat');
    }
    return i18next.t('videoconference.controlpanel.open_chat');
};

export const getTextHoverVideo = (videoAllowed, videoActive, i18next) => {
    if (!videoAllowed) {
        return i18next.t('videoconference.controlpanel.no_camera_available');
    } else {
        return videoActive
            ? i18next.t('videoconference.controlpanel.turn_camera_off') +
                  getHoverTextShortcutVideo()
            : i18next.t('videoconference.controlpanel.turn_camera_on') +
                  getHoverTextShortcutVideo();
    }
};

export const getTextHoverScreenShare = (
    hasElectronScreenShareAccess,
    otherAttendeeIsScreenSharing,
    screenShareActive,
    isTeamsFullClient,
    i18next,
) => {
    if (isElectron() && !hasElectronScreenShareAccess) {
        return i18next.t('videoconference.controlpanel.no_screenshare_rights');
    } else if (isTeamsFullClient) {
        return '';
    } else if (otherAttendeeIsScreenSharing) {
        return i18next.t('videoconference.controlpanel.screenshare_occupied');
    } else {
        return screenShareActive
            ? i18next.t('videoconference.controlpanel.stop_screenshare')
            : i18next.t('videoconference.controlpanel.start_screenshare');
    }
};

export const getTextHoverMic = (audioActive, i18next) => {
    return audioActive
        ? i18next.t('videoconference.controlpanel.turn_mic_off') +
              getHoverTextShortcutMic()
        : i18next.t('videoconference.controlpanel.turn_mic_on') +
              getHoverTextShortcutMic();
};

const getHoverTextShortcutVideo = () => {
    if (isMacOs()) {
        return ' (\u2318 + E)';
    }
    if (isWindowsOs()) {
        return ' (Ctrl + E)';
    }
    return '';
};
